import { Children, Fragment, cloneElement, isValidElement, useRef } from 'react';
import { DotsThree } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { defaultPrimaryActions, directActionOptions, rowActionKey } from '@/lib/documentActions';

const flatten = children => Children.toArray(children).flatMap(child => isValidElement(child) && child.type === Fragment ? flatten(child.props.children) : [child]);

// Shared presentation only: original permission conditions and handlers stay on each page.
export const InvoiceRowActions = ({ documentId, docType = 'factuur', primaryActions, children }) => {
  const selected = useRef(false);
  const all = flatten(children).filter(isValidElement);
  const isCommand = child => ['button', 'a'].includes(child.type) || child.props.testid?.startsWith('remote-print-');
  const commands = all.filter(isCommand);
  const badges = all.filter(child => !isCommand(child) && child.props['data-testid']);
  const options = directActionOptions(docType);
  const keyOf = child => rowActionKey(child, documentId);
  const labelOf = child => options.find(o => o.key === keyOf(child))?.label || child.props.title || 'Openen';
  const extraOf = child => Children.toArray(child.props.children).filter(el => isValidElement(el) && el.props['data-testid']);
  const primary = (primaryActions || defaultPrimaryActions(docType)).map(key => commands.find(child => keyOf(child) === key)).filter(Boolean);
  const other = commands.filter(child => !primary.includes(child));
  return <div className="document-action-group">
    <div className="invoice-row-actions" data-testid={`invoice-actions-${documentId}`}>
      {primary.map(child => {
        if (keyOf(child) === 'print') return cloneElement(child, { key: `${documentId}:print`, compactIcon: true });
        const icon = Children.toArray(child.props.children).find(isValidElement);
        const extra = extraOf(child);
        const title = (child.props.title || labelOf(child)) + (extra.length ? ` (${extra.map(el => el.props.children).join(', ')})` : '');
        return cloneElement(child, { key: `${documentId}:${keyOf(child)}`, className: `invoice-action-icon${extra.length ? ' relative' : ''}`, title, 'aria-label': title }, icon, extra.map(el => cloneElement(el, { key: el.props['data-testid'], className: 'sr-only' })));
      })}
      {!commands.length && <span className="text-gray-400" data-testid={`invoice-actions-empty-${documentId}`}>—</span>}
      {other.length > 0 && <DropdownMenu>
        <DropdownMenuTrigger asChild><button type="button" className="invoice-action-icon" title="Meer acties" aria-label={`Meer acties voor ${documentId}`}
          data-testid={`more-actions-trigger-${documentId}`}><DotsThree size={20} weight="bold" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={6} collisionPadding={8} className="invoice-overflow-menu"
          onClick={e => e.stopPropagation()} onCloseAutoFocus={e => { if (selected.current) { e.preventDefault(); selected.current = false; } }}
          data-testid={`more-actions-menu-${documentId}`}>
          {other.map((child, index) => {
            if (keyOf(child) === 'print') return cloneElement(child, { key: 'print', menuItem: true, label: 'Afdrukken' });
            const id = child.props['data-testid'];
            const label = labelOf(child);
            const icon = Children.toArray(child.props.children).find(isValidElement);
            const danger = ['del', 'shop-reject'].includes(keyOf(child));
            return <Fragment key={id || index}>
              {danger && <DropdownMenuSeparator />}
              <DropdownMenuItem asChild onSelect={() => { selected.current = true; }}>
                {cloneElement(child, { className: `invoice-overflow-action ${danger ? 'text-red-700 focus:text-red-700' : ''}`, 'aria-label': label }, icon, <span>{label}</span>, extraOf(child))}
              </DropdownMenuItem>
            </Fragment>;
          })}
        </DropdownMenuContent>
      </DropdownMenu>}
    </div>
    {badges.length > 0 && <div className="flex items-center justify-end gap-1 flex-wrap mt-1" data-testid={`document-action-status-${documentId}`}>{badges.map(child => cloneElement(child, { key: child.props['data-testid'] }))}</div>}
  </div>;
};